import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Briefcase, Building, FileText, Award, ShieldCheck, CalendarX,
  Plus, Edit, Trash2, RefreshCw, Download, Check, X, Search,
  ChevronRight, Users, Palette, ToggleRight, Eye, EyeOff
} from 'lucide-react';

const API = API_URL;

const ICONS = {
  briefcase: Briefcase, building: Building, 'file-text': FileText,
  award: Award, 'shield-check': ShieldCheck, 'calendar-x': CalendarX,
};

const COLORS = [
  { name: 'Bleu', value: '#3b82f6' }, { name: 'Vert', value: '#22c55e' },
  { name: 'Rouge', value: '#ef4444' }, { name: 'Orange', value: '#f97316' },
  { name: 'Violet', value: '#8b5cf6' }, { name: 'Rose', value: '#ec4899' },
  { name: 'Cyan', value: '#06b6d4' }, { name: 'Gris', value: '#6b7280' },
];

export default function LookupsPage() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId } = useTenant();
  const navigate = useNavigate();
  const fr = language === 'fr';

  const [lookupTypes, setLookupTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', color: '', sortOrder: 0, active: true });
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await axios.get(`${API}/lookups/types`);
        setLookupTypes(response.data);
        if (response.data.length > 0) setSelectedType(response.data[0]);
      } catch (error) { console.error('Failed to fetch lookup types:', error); }
      finally { setLoading(false); }
    };
    fetchTypes();
  }, []);

  const fetchItems = useCallback(async () => {
    if (!selectedType || !effectiveTenantId) return;
    setLoadingItems(true);
    try {
      const response = await axios.get(`${API}/lookups/${selectedType.type}?tenantId=${effectiveTenantId}&includeInactive=${showInactive}`);
      setItems(response.data.items || []);
    } catch (error) { console.error('Failed to fetch items:', error); }
    finally { setLoadingItems(false); }
  }, [selectedType, effectiveTenantId, showInactive]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const filteredItems = useMemo(() =>
    items.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()) || (item.code && item.code.toLowerCase().includes(searchTerm.toLowerCase()))),
    [items, searchTerm]
  );

  const kpis = useMemo(() => {
    const active = items.filter(i => i.active !== false).length;
    const inactive = items.filter(i => i.active === false).length;
    const totalUsage = items.reduce((s, i) => s + (i.usageCount || 0), 0);
    return { total: items.length, active, inactive, totalUsage };
  }, [items]);

  const getIcon = (iconName) => ICONS[iconName] || Briefcase;

  const openNewDialog = () => {
    setEditingItem(null);
    setFormData({ name: '', code: '', description: '', color: '', sortOrder: items.length, active: true });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({ name: item.name, code: item.code || '', description: item.description || '', color: item.color || '', sortOrder: item.sortOrder || 0, active: item.active !== false });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { toast.error(fr ? 'Le nom est requis' : 'Name is required'); return; }
    setSaving(true);
    try {
      if (editingItem) {
        await axios.put(`${API}/lookups/${selectedType.type}/${editingItem.id}`, formData);
        toast.success(fr ? 'Élément modifié' : 'Item updated');
      } else {
        await axios.post(`${API}/lookups/${selectedType.type}?tenantId=${effectiveTenantId}`, formData);
        toast.success(fr ? 'Élément créé' : 'Item created');
      }
      setDialogOpen(false); fetchItems();
    } catch (error) { toast.error(error.response?.data?.detail || (fr ? 'Erreur' : 'Error')); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!confirm(fr ? `Supprimer "${item.name}" ?` : `Delete "${item.name}"?`)) return;
    try {
      const response = await axios.delete(`${API}/lookups/${selectedType.type}/${item.id}`);
      if (response.data.deactivated) {
        toast.info(fr ? `Élément désactivé (utilisé par ${response.data.usageCount} employés)` : `Item deactivated (used by ${response.data.usageCount} employees)`);
      } else {
        toast.success(fr ? 'Élément supprimé' : 'Item deleted');
      }
      fetchItems();
    } catch (error) { toast.error(fr ? 'Erreur lors de la suppression' : 'Delete failed'); }
  };

  const handleToggleActive = async (item) => {
    try {
      await axios.put(`${API}/lookups/${selectedType.type}/${item.id}`, { active: item.active === false });
      toast.success(item.active === false ? (fr ? 'Élément réactivé' : 'Item reactivated') : (fr ? 'Élément désactivé' : 'Item deactivated'));
      fetchItems();
    } catch (error) { toast.error(fr ? 'Erreur' : 'Error'); }
  };

  const handleImport = async () => {
    if (!selectedType?.employeeField) return;
    setImporting(true);
    try {
      const response = await axios.post(`${API}/lookups/${selectedType.type}/import-existing?tenantId=${effectiveTenantId}`, {});
      if (response.data.imported > 0) { toast.success(fr ? `${response.data.imported} élément(s) importé(s)` : `${response.data.imported} item(s) imported`); fetchItems(); }
      else toast.info(fr ? 'Aucun nouvel élément à importer' : 'No new items to import');
    } catch (error) { toast.error(fr ? 'Erreur lors de l\'import' : 'Import failed'); }
    finally { setImporting(false); }
  };

  const navigateToEmployees = (itemName) => {
    // Navigate to employees page - the field depends on the lookup type
    navigate(`/admin/employees`);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-primary" size={32} /></div>;

  return (
    <div className="space-y-4" data-testid="lookups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{fr ? 'Listes de référence' : 'Reference Lists'}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Postes, départements et autres listes déroulantes' : 'Job titles, departments and dropdown lists'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Type Sidebar */}
        <Card className="lg:col-span-1 dark:border-slate-700" data-testid="type-sidebar">
          <CardHeader className="py-2 px-3 border-b dark:border-slate-700">
            <CardTitle className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase">{fr ? 'Types' : 'Types'}</CardTitle>
          </CardHeader>
          <CardContent className="p-1.5">
            <div className="space-y-0.5">
              {lookupTypes.map((type) => {
                const Icon = getIcon(type.icon);
                const isSelected = selectedType?.type === type.type;
                return (
                  <button key={type.type} onClick={() => setSelectedType(type)}
                    className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg text-left transition-all text-sm ${isSelected ? 'bg-primary text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300'}`}
                    data-testid={`type-${type.type}`}>
                    <Icon size={16} />
                    <span className="font-medium">{fr ? type.name : type.nameEn}</span>
                    <ChevronRight size={14} className="ml-auto opacity-50" />
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Items Panel */}
        <div className="lg:col-span-3 space-y-3">
          {/* KPIs */}
          {selectedType && (
            <div className="grid grid-cols-4 gap-2" data-testid="lookup-kpis">
              <div className="flex items-center gap-2 p-2.5 rounded-lg border dark:border-slate-700 bg-blue-50/50 dark:bg-blue-950/20">
                {React.createElement(getIcon(selectedType.icon), { size: 16, className: 'text-blue-600 dark:text-blue-400' })}
                <div><p className="text-base font-bold text-slate-800 dark:text-white">{kpis.total}</p><p className="text-[9px] text-slate-500 dark:text-slate-400 uppercase">Total</p></div>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg border dark:border-slate-700 bg-green-50/50 dark:bg-green-950/20">
                <Check size={16} className="text-green-600 dark:text-green-400" />
                <div><p className="text-base font-bold text-slate-800 dark:text-white">{kpis.active}</p><p className="text-[9px] text-slate-500 dark:text-slate-400 uppercase">{fr ? 'Actifs' : 'Active'}</p></div>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg border dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/20">
                <X size={16} className="text-slate-500 dark:text-slate-400" />
                <div><p className="text-base font-bold text-slate-800 dark:text-white">{kpis.inactive}</p><p className="text-[9px] text-slate-500 dark:text-slate-400 uppercase">{fr ? 'Inactifs' : 'Inactive'}</p></div>
              </div>
              <div className="flex items-center gap-2 p-2.5 rounded-lg border dark:border-slate-700 bg-purple-50/50 dark:bg-purple-950/20">
                <Users size={16} className="text-purple-600 dark:text-purple-400" />
                <div><p className="text-base font-bold text-slate-800 dark:text-white">{kpis.totalUsage}</p><p className="text-[9px] text-slate-500 dark:text-slate-400 uppercase">{fr ? 'Utilisations' : 'Uses'}</p></div>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <Card className="dark:border-slate-700">
            <CardContent className="p-3">
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={fr ? 'Rechercher...' : 'Search...'} className="pl-8 h-8 text-sm" data-testid="search-input" />
                </div>
                <div className="flex items-center gap-1.5">
                  <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive" data-testid="show-inactive-toggle" />
                  <label htmlFor="show-inactive" className="text-xs text-slate-500 dark:text-slate-400 cursor-pointer whitespace-nowrap">{fr ? 'Inactifs' : 'Inactive'}</label>
                </div>
                {selectedType?.employeeField && (
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={handleImport} disabled={importing} data-testid="import-btn">
                    {importing ? <RefreshCw className="animate-spin" size={12} /> : <Download size={12} />}
                    {fr ? 'Importer' : 'Import'}
                  </Button>
                )}
                <Button size="sm" className="h-8 text-xs gap-1" onClick={openNewDialog} data-testid="add-item-btn">
                  <Plus size={12} /> {fr ? 'Ajouter' : 'Add'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="dark:border-slate-700" data-testid="items-table">
            <CardContent className="p-0">
              {loadingItems ? (
                <div className="flex items-center justify-center py-12"><RefreshCw className="animate-spin text-primary" size={24} /></div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <Briefcase size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Aucun élément trouvé' : 'No items found'}</p>
                  {selectedType?.employeeField && (
                    <Button variant="outline" size="sm" className="mt-3 gap-1 text-xs" onClick={handleImport} data-testid="import-from-employees-btn">
                      <Download size={12} /> {fr ? 'Importer depuis les employés' : 'Import from employees'}
                    </Button>
                  )}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/80 dark:bg-slate-800/80">
                      <TableHead className="text-xs font-bold py-2">{fr ? 'Nom' : 'Name'}</TableHead>
                      <TableHead className="text-xs font-bold py-2">{fr ? 'Code' : 'Code'}</TableHead>
                      <TableHead className="text-xs font-bold py-2">{fr ? 'Couleur' : 'Color'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-center">{fr ? 'Utilisé par' : 'Used by'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-center">{fr ? 'Statut' : 'Status'}</TableHead>
                      <TableHead className="text-xs font-bold py-2 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item, idx) => (
                      <TableRow key={item.id} className={`${idx % 2 === 1 ? 'bg-slate-25 dark:bg-slate-900/30' : ''} ${item.active === false ? 'opacity-60' : ''}`} data-testid={`item-${item.id}`}>
                        <TableCell className="py-2">
                          <div className="flex items-center gap-2">
                            {item.color && <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: item.color }} />}
                            <div>
                              <span className="font-medium text-sm text-slate-900 dark:text-slate-100">{item.name}</span>
                              {item.description && <p className="text-[10px] text-slate-500 dark:text-slate-400">{item.description}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-sm text-slate-600 dark:text-slate-400 font-mono">{item.code || '-'}</TableCell>
                        <TableCell className="py-2">
                          {item.color ? <div className="w-5 h-5 rounded border dark:border-slate-600" style={{ backgroundColor: item.color }} /> : <span className="text-slate-400">-</span>}
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          {(item.usageCount || 0) > 0 ? (
                            <Badge className="bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 text-[10px] cursor-pointer hover:bg-purple-200 dark:hover:bg-purple-900/60"
                              onClick={() => navigateToEmployees(item.name)} data-testid={`usage-${item.id}`}>
                              <Users size={10} className="mr-0.5" /> {item.usageCount}
                            </Badge>
                          ) : <span className="text-slate-400 text-xs">0</span>}
                        </TableCell>
                        <TableCell className="py-2 text-center">
                          <button onClick={() => handleToggleActive(item)} className="inline-flex" data-testid={`toggle-${item.id}`}>
                            {item.active !== false ? (
                              <Badge className="bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 text-[10px] hover:bg-green-200 cursor-pointer"><Check size={10} className="mr-0.5" /> {fr ? 'Actif' : 'Active'}</Badge>
                            ) : (
                              <Badge className="bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 text-[10px] hover:bg-slate-200 cursor-pointer"><X size={10} className="mr-0.5" /> {fr ? 'Inactif' : 'Inactive'}</Badge>
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          <div className="flex gap-0.5 justify-end">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEditDialog(item)} data-testid={`edit-${item.id}`}><Edit size={14} className="text-slate-500" /></Button>
                            {(item.usageCount || 0) === 0 && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20" onClick={() => handleDelete(item)} data-testid={`delete-${item.id}`}><Trash2 size={14} /></Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">{React.createElement(getIcon(selectedType?.icon), { size: 18 })} {editingItem ? (fr ? 'Modifier' : 'Edit') : (fr ? 'Nouvel élément' : 'New Item')}</DialogTitle>
            <DialogDescription>{selectedType && (fr ? selectedType.name : selectedType.nameEn)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-xs">{fr ? 'Nom' : 'Name'} *</Label>
              <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder={fr ? 'Ex: Chef d\'équipe' : 'Ex: Team Leader'} className="h-8 text-sm" data-testid="item-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">{fr ? 'Code' : 'Code'}</Label>
                <Input value={formData.code} onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })} placeholder="CHE" maxLength={10} className="h-8 text-sm font-mono" data-testid="item-code-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs">{fr ? 'Description' : 'Description'}</Label>
                <Input value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className="h-8 text-sm" data-testid="item-description-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1"><Palette size={12} /> {fr ? 'Couleur' : 'Color'}</Label>
              <div className="flex flex-wrap gap-1.5">
                {COLORS.map(color => (
                  <button key={color.value} type="button" onClick={() => setFormData({ ...formData, color: color.value })}
                    className={`w-7 h-7 rounded-full border-2 transition-transform ${formData.color === color.value ? 'border-slate-900 dark:border-white scale-110' : 'border-transparent hover:scale-105'}`}
                    style={{ backgroundColor: color.value }} title={color.name} />
                ))}
                {formData.color && (
                  <button type="button" onClick={() => setFormData({ ...formData, color: '' })}
                    className="w-7 h-7 rounded-full border-2 border-dashed border-slate-300 dark:border-slate-600 flex items-center justify-center text-slate-400"><X size={12} /></button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={formData.active} onCheckedChange={v => setFormData({ ...formData, active: v })} id="item-active" />
              <label htmlFor="item-active" className="text-sm text-slate-700 dark:text-slate-300 cursor-pointer">{fr ? 'Actif' : 'Active'}</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{fr ? 'Annuler' : 'Cancel'}</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-item-btn">
              {saving && <RefreshCw className="animate-spin mr-1" size={12} />}
              {fr ? 'Enregistrer' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
